import { Suspense, lazy } from 'react';

import { BuildingTooltipLayer } from '@/components/BuildingTooltipLayer';
import { CenterBookPanel } from '@/components/CenterBookPanel';
import { Leaderboard } from '@/components/Leaderboard';
import { RiskOfficerCameo } from '@/components/RiskOfficerCameo';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useDashboardStore } from '@/state/store';

/**
 * Pixi-backed building exterior view.
 *
 * Layout adapts per breakpoint:
 *   desktop ─ canvas + 260px right sidebar with the leaderboard
 *   tablet  ─ canvas + 220px right sidebar
 *   mobile  ─ canvas on top, leaderboard collapses to a strip below
 *
 * The Pixi-canvas wrapper is loaded lazily so the initial bundle stays
 * lean for users that prefer the DOM debug renderer.
 */
const PixiCanvas = lazy(() =>
  import('@/components/PixiCanvas').then((m) => ({ default: m.PixiCanvas })),
);

export function BuildingPixiView() {
  const bp = useBreakpoint();
  const leaderboard = useDashboardStore((s) => s.leaderboard);

  const isMobile = bp === 'mobile';
  const sidebarWidth = bp === 'tablet' ? 220 : 260;
  // Sidebar always contains CenterBookPanel.  Leaderboard sits below
  // it only when it has data (i.e. ``leaderboard.computed`` has fired
  // — Fridays at 15:50 UTC); the rest of the week it was wasted space.
  const showLeaderboard = leaderboard !== null;
  // Sidebar is desktop/tablet only.  On mobile the CenterBook and
  // event-feed both live in ``<MobileBottomBar>`` as chip strips with
  // bottom-sheets on tap, freeing the canvas to use the full viewport
  // height -- otherwise the pod windows are unreadable on a phone.
  const showSidebar = !isMobile;

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? 'minmax(0, 1fr)'
          : `minmax(0, 1fr) ${sidebarWidth}px`,
        gridTemplateRows: '1fr',
        gap: 0,
        minHeight: 0,
        minWidth: 0,
        // Clip any canvas overflow.  Pixi's ``resizeTo: host`` usually
        // tracks the cell, but on iOS Safari there's a window between
        // initial paint and the first resize where the canvas can be
        // a few px taller than its grid cell -- without this, the
        // overflow pushed the adjacent ``MobileBottomBar`` below the
        // visible area on real devices (works in dev-tools emulator
        // because there's no real address-bar dvh jitter).
        overflow: 'hidden',
      }}
    >
      <div style={{ minHeight: 0, position: 'relative' }}>
        <Suspense
          fallback={
            <div
              style={{
                padding: 24,
                color: '#8b949e',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading building canvas…
            </div>
          }
        >
          <PixiCanvas />
        </Suspense>
        <BuildingTooltipLayer />
        <RiskOfficerCameo />
      </div>
      {showSidebar ? (
        <div
          style={{
            padding: 16,
            borderLeft: '1px solid #21262d',
            background: '#0d1117',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflowY: 'auto',
          }}
        >
          <CenterBookPanel />
          {showLeaderboard ? <Leaderboard /> : null}
        </div>
      ) : null}
    </section>
  );
}
